"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-storage-capacity-calculator",
  "freezer-energy-cost-calculator",
  "freezer-vacuum-sealer-calculator",
  "food-storage-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Freezer Meat Storage Calculator",
  category: "Calculator" as const,
  mediumDescription:
    "Buying a quarter, half, or whole animal? Estimate the freezer space you need and the maximum USDA-recommended storage time. Sized using a 35–40 lb-per-cu-ft chest-freezer rule of thumb and USDA Food Safety meat-storage guidelines.",
  faqs: [
    { question: "How much freezer space does a quarter beef need?", answer: "A quarter beef averages about 150 lbs of cut-and-wrapped meat, which needs roughly 4 cu ft of chest-freezer space at 35–40 lbs per cu ft." },
    { question: "How long can frozen beef stay safe in the freezer?", answer: "USDA Food Safety guidance: ground beef keeps best quality for 3–4 months at 0°F; steaks and roasts 6–12 months. Beyond that it's still safe — just lower quality." },
    { question: "Why does pork have a shorter freezer life than beef?", answer: "USDA recommends 4–6 months for whole-pig cuts because pork fat oxidizes faster than beef fat. Ground pork is 1–2 months." },
    { question: "Should I get a chest or upright freezer for bulk meat?", answer: "Chest freezers are the standard recommendation for bulk meat: better $-per-cu-ft, ~20% lower energy use, and they hold cold longer during outages. Trade-off is harder organization." },
    { question: "How should I package meat for the freezer?", answer: "Vacuum sealing is best — it can extend storage life 3–5x by preventing freezer burn. Otherwise, double-wrap in freezer paper plus a freezer bag and label everything with cut and date." },
    { question: "What temperature should I keep my freezer at?", answer: "USDA recommends 0°F (-18°C) for long-term storage. At 0°F, food stays safe indefinitely; the storage windows above are about quality, not safety." },
  ],
  relatedTools: ["freezer-storage-capacity-calculator", "freezer-energy-cost-calculator", "freezer-vacuum-sealer-calculator"],
}

interface MeatBreakdown {
  cut: string
  pounds: number
  percentage: number
}

export function BulkMeatStorageCalculatorPageClient() {
  const [meatType, setMeatType] = useState("beef-quarter")
  const [quantity, setQuantity] = useState("1")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const qty = readCount(quantity, { min: 1, max: 20 })
    if (qty === null) return null

    let totalPounds = 0
    let breakdown: MeatBreakdown[] = []

    if (meatType === "beef-quarter") {
      totalPounds = 150 * qty
      breakdown = [
        { cut: "Steaks (ribeye, sirloin, T-bone)", pounds: 45, percentage: 30 },
        { cut: "Roasts (chuck, rump, round)", pounds: 38, percentage: 25 },
        { cut: "Ground beef", pounds: 45, percentage: 30 },
        { cut: "Stew meat & short ribs", pounds: 15, percentage: 10 },
        { cut: "Bones & soup bones", pounds: 7, percentage: 5 },
      ]
    } else if (meatType === "beef-half") {
      totalPounds = 300 * qty
      breakdown = [
        { cut: "Steaks (ribeye, sirloin, T-bone)", pounds: 90, percentage: 30 },
        { cut: "Roasts (chuck, rump, round)", pounds: 75, percentage: 25 },
        { cut: "Ground beef", pounds: 90, percentage: 30 },
        { cut: "Stew meat & short ribs", pounds: 30, percentage: 10 },
        { cut: "Bones & soup bones", pounds: 15, percentage: 5 },
      ]
    } else if (meatType === "beef-whole") {
      totalPounds = 600 * qty
      breakdown = [
        { cut: "Steaks (ribeye, sirloin, T-bone)", pounds: 180, percentage: 30 },
        { cut: "Roasts (chuck, rump, round)", pounds: 150, percentage: 25 },
        { cut: "Ground beef", pounds: 180, percentage: 30 },
        { cut: "Stew meat & short ribs", pounds: 60, percentage: 10 },
        { cut: "Bones & soup bones", pounds: 30, percentage: 5 },
      ]
    } else if (meatType === "pork-half") {
      totalPounds = 90 * qty
      breakdown = [
        { cut: "Pork chops", pounds: 27, percentage: 30 },
        { cut: "Ham & roasts", pounds: 27, percentage: 30 },
        { cut: "Bacon & sausage", pounds: 18, percentage: 20 },
        { cut: "Ribs", pounds: 9, percentage: 10 },
        { cut: "Ground pork & trim", pounds: 9, percentage: 10 },
      ]
    } else if (meatType === "pork-whole") {
      totalPounds = 180 * qty
      breakdown = [
        { cut: "Pork chops", pounds: 54, percentage: 30 },
        { cut: "Ham & roasts", pounds: 54, percentage: 30 },
        { cut: "Bacon & sausage", pounds: 36, percentage: 20 },
        { cut: "Ribs", pounds: 18, percentage: 10 },
        { cut: "Ground pork & trim", pounds: 18, percentage: 10 },
      ]
    } else if (meatType === "lamb-whole") {
      totalPounds = 40 * qty
      breakdown = [
        { cut: "Leg & shoulder roasts", pounds: 16, percentage: 40 },
        { cut: "Chops (loin, rib)", pounds: 12, percentage: 30 },
        { cut: "Ground lamb", pounds: 8, percentage: 20 },
        { cut: "Shanks & stew meat", pounds: 4, percentage: 10 },
      ]
    } else {
      totalPounds = 50 * qty
      breakdown = [
        { cut: "Steaks (backstrap, tenderloin)", pounds: 15, percentage: 30 },
        { cut: "Roasts", pounds: 12.5, percentage: 25 },
        { cut: "Ground venison", pounds: 17.5, percentage: 35 },
        { cut: "Stew meat", pounds: 5, percentage: 10 },
      ]
    }

    const cubicFeetNeeded = totalPounds / 37.5

    let freezerSize = ""
    if (cubicFeetNeeded <= 5) freezerSize = "5–7 cu ft compact chest freezer"
    else if (cubicFeetNeeded <= 10) freezerSize = "10–12 cu ft medium chest freezer"
    else if (cubicFeetNeeded <= 15) freezerSize = "15–17 cu ft large chest freezer"
    else freezerSize = "20+ cu ft extra-large chest freezer"

    const storageTime = meatType.includes("beef")
      ? "6–12 months"
      : meatType.includes("pork")
      ? "4–6 months"
      : meatType.includes("lamb")
      ? "6–9 months"
      : "8–12 months"

    return {
      totalPounds,
      cubicFeetNeeded,
      freezerSize,
      breakdown,
      storageTime,
    }
  }, [meatType, quantity])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="How big a freezer for a"
      heroTitleItalic="quarter cow,"
      heroTitlePost="half pig, or whole deer?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~20 sec", "USDA storage windows + chest-freezer density"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "37.5", label: "lbs / cu ft", note: "Chest packed" },
            { value: "150 lb", label: "Quarter beef", note: "Cut & wrapped" },
            { value: "12 mo", label: "Beef max", note: "USDA quality" },
            { value: "0°F", label: "Storage temp", note: "USDA target" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "freezer-meat-storage-calculator")}
      cta={{
        title: "Ready to size the chest freezer for that bulk order?",
        body: "Browse chest freezers — denser packing, more $-per-cu-ft, longer cold-hold during outages.",
        buttonLabel: "Browse chest freezers",
        buttonHref: "/collections/chest-freezers",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Animal.
                <br />
                Quantity. Plan.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Hanging weights and cut-and-wrapped yields based on USDA primal-cut averages.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Meat type
                  </label>
                  <select
                    value={meatType}
                    onChange={(e) => setMeatType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="beef-quarter">1/4 beef cow (~150 lb)</option>
                    <option value="beef-half">1/2 beef cow (~300 lb)</option>
                    <option value="beef-whole">Whole beef cow (~600 lb)</option>
                    <option value="pork-half">1/2 pig (~90 lb)</option>
                    <option value="pork-whole">Whole pig (~180 lb)</option>
                    <option value="lamb-whole">Whole lamb (~40 lb)</option>
                    <option value="deer-whole">Whole deer (~50 lb)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Quantity
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={20}
                      step="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">animals</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Splitting a side with neighbors? Use fractional units.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Freezer space needed
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.cubicFeetNeeded.toFixed(1) ?? "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">cu ft</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `${result.freezerSize}. Storage life: ${result.storageTime} at 0°F.` : "Pick a meat type to size your freezer."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Total weight</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.totalPounds ?? "—"} lb</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">USDA quality life</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.storageTime ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {result && (
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-200">
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#ff9951] mb-3">
                Approximate cut breakdown
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {result.breakdown.map((b) => (
                  <div key={b.cut} className="flex items-center justify-between border-b border-gray-100 py-2">
                    <span className="text-[14px] text-gray-700">{b.cut}</span>
                    <span className="text-[13px] font-mono font-bold text-gray-900">{b.pounds} lb · {b.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pick the box"
          title="Chest freezers built for bulk meat"
          description="Denser packing (~37.5 lb/cu ft), 20% less energy than uprights, and longer cold-hold during outages."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                USDA storage. <em className="italic text-[#ff9951]">Real</em> cuts.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Cut-and-wrapped yields from USDA primal-cut averages; storage windows from USDA Food Safety and Inspection Service guidance for frozen meat at 0°F.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Cut-and-wrapped weight", d: "Quarter beef ~150 lb, half ~300 lb, whole ~600 lb. Half pig ~90 lb, whole ~180 lb. Whole lamb ~40 lb, whole deer ~50 lb." },
                { n: "02", t: "Volume conversion", d: "Chest freezers store roughly 35–40 lb per cu ft when packed (we use 37.5 lb/cu ft). Uprights are ~30 lb/cu ft due to shelving." },
                { n: "03", t: "USDA storage windows", d: "Beef 6–12 mo, pork 4–6 mo, lamb 6–9 mo, venison 8–12 mo. Ground meats are shorter. All values assume 0°F (-18°C)." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Upright freezers if organization beats density"
          description="Easier shelf access, smaller footprint — trade-off is ~20% more energy use and ~30 lb/cu ft packing density."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
